/**
 * Bilt:Spec user onboarding — pure helpers (versioned, dismissible, role-aware).
 */

export const ONBOARDING_VERSION = 2;

export type OnboardingRole = 'builder' | 'organizer' | 'judge' | 'admin' | 'club_admin' | 'shop';

export interface OnboardingState {
  version: number;
  started_at: string | null;
  skipped_at: string | null;
  welcome_dismissed_at: string | null;
  checklist_dismissed_at: string | null;
  organizer_checklist_dismissed_at: string | null;
  judge_guide_dismissed_at: string | null;
  club_guide_dismissed_at: string | null;
  shop_tip_dismissed_at: string | null;
  completed_at: string | null;
  last_step: string | null;
  tips_dismissed: string[];
  steps: Record<string, boolean>;
}

export interface ChecklistStep {
  id: string;
  label: string;
  done: boolean;
  href?: string;
  actionId?: string;
}

export function defaultOnboardingState(): OnboardingState {
  return {
    version: ONBOARDING_VERSION,
    started_at: null,
    skipped_at: null,
    welcome_dismissed_at: null,
    checklist_dismissed_at: null,
    organizer_checklist_dismissed_at: null,
    judge_guide_dismissed_at: null,
    club_guide_dismissed_at: null,
    shop_tip_dismissed_at: null,
    completed_at: null,
    last_step: null,
    tips_dismissed: [],
    steps: {},
  };
}

export function normalizeOnboardingState(raw: unknown): OnboardingState {
  const base = defaultOnboardingState();
  if (!raw || typeof raw !== 'object') return base;
  const o = raw as Record<string, unknown>;
  return {
    version: typeof o.version === 'number' ? o.version : ONBOARDING_VERSION,
    started_at: (o.started_at as string) || null,
    skipped_at: (o.skipped_at as string) || null,
    welcome_dismissed_at: (o.welcome_dismissed_at as string) || null,
    checklist_dismissed_at: (o.checklist_dismissed_at as string) || null,
    organizer_checklist_dismissed_at: (o.organizer_checklist_dismissed_at as string) || null,
    judge_guide_dismissed_at: (o.judge_guide_dismissed_at as string) || null,
    club_guide_dismissed_at: (o.club_guide_dismissed_at as string) || null,
    shop_tip_dismissed_at: (o.shop_tip_dismissed_at as string) || null,
    completed_at: (o.completed_at as string) || null,
    last_step: typeof o.last_step === 'string' ? o.last_step : null,
    tips_dismissed: Array.isArray(o.tips_dismissed)
      ? o.tips_dismissed.map(String)
      : [],
    steps:
      o.steps && typeof o.steps === 'object' && !Array.isArray(o.steps)
        ? Object.fromEntries(
            Object.entries(o.steps as Record<string, unknown>).map(([k, v]) => [k, Boolean(v)])
          )
        : {},
  };
}

export function isTipDismissed(state: OnboardingState, tipId: string): boolean {
  return state.tips_dismissed.includes(tipId);
}

export function shouldShowWelcome(state: OnboardingState, vehicleCount: number): boolean {
  if (state.welcome_dismissed_at) return false;
  if (state.skipped_at) return false;
  if (state.completed_at) return false;
  return vehicleCount === 0;
}

export function hasText(value?: string | null): boolean {
  return typeof value === 'string' && value.trim() !== '';
}

export interface BuilderVehicleSnapshot {
  id: number;
  username?: string;
  slugPath?: string;
  engine?: string | null;
  transmission?: string | null;
  drivetrain?: string | null;
  chassis?: string | null;
  description?: string | null;
  imageCount?: number;
  modificationCount?: number;
  completenessPercent?: number;
  isPublic?: boolean;
}

export function buildBuilderChecklist(
  vehicles: BuilderVehicleSnapshot[],
  state: OnboardingState,
  username: string
): ChecklistStep[] {
  const first = vehicles[0];
  const vehiclePath = first?.slugPath
    ? first.slugPath
    : first && username
      ? `/${username}`
      : '/';

  const specsDone = first
    ? [first.engine, first.transmission, first.drivetrain, first.chassis].filter((v) =>
        hasText(v)
      ).length >= 2
    : false;

  const photoDone = (first?.imageCount ?? 0) > 0;
  const modDone = (first?.modificationCount ?? 0) > 0;
  const summaryDone = hasText(first?.description) || Boolean(state.steps.build_summary);
  const shareDone =
    Boolean(state.steps.share_opened) ||
    Boolean(first?.isPublic) ||
    (first?.completenessPercent ?? 0) >= 100;

  return [
    {
      id: 'vehicle',
      label: 'Add your first vehicle',
      done: vehicles.length > 0,
      actionId: 'add_vehicle',
    },
    {
      id: 'photo',
      label: 'Add a primary photo',
      done: photoDone,
      href: vehicles.length ? vehiclePath : undefined,
      actionId: vehicles.length ? undefined : 'add_vehicle',
    },
    {
      id: 'specs',
      label: 'Complete core specifications',
      done: specsDone,
      href: vehicles.length ? vehiclePath : undefined,
    },
    {
      id: 'mods',
      label: 'Add or import modifications',
      done: modDone,
      href: vehicles.length ? vehiclePath : undefined,
    },
    {
      id: 'summary',
      label: 'Write a short build summary',
      done: summaryDone,
      href: vehicles.length ? vehiclePath : undefined,
    },
    {
      id: 'share',
      label: 'Share the vehicle profile',
      done: shareDone,
      href: vehicles.length ? vehiclePath : undefined,
      actionId: 'share',
    },
  ];
}

export function buildOrganizerChecklist(
  events: Array<{
    id: number;
    description?: string | null;
    location?: string | null;
    city?: string | null;
    status?: string;
  }>,
  state: OnboardingState
): ChecklistStep[] {
  const hasEvent = events.length > 0;
  const firstId = events[0]?.id;
  const detailsDone = events.some(
    (e) => hasText(e.description) || hasText(e.location) || hasText(e.city)
  );
  const publishedDone = events.some((e) => e.status === 'published' || e.status === 'completed');
  const classesDone = Boolean(state.steps.organizer_classes);
  const regsDone = Boolean(state.steps.organizer_registrations);
  const judgesDone = Boolean(state.steps.organizer_judges);

  return [
    {
      id: 'create',
      label: 'Create your first event',
      done: hasEvent,
      href: '/organizer/events/new',
    },
    {
      id: 'details',
      label: 'Add event details',
      done: detailsDone,
      href: firstId ? `/organizer/events/${firstId}` : '/organizer/events/new',
    },
    {
      id: 'classes',
      label: 'Add classes',
      done: classesDone,
      href: firstId ? `/organizer/events/${firstId}` : undefined,
    },
    {
      id: 'publish',
      label: 'Publish event',
      done: publishedDone,
      href: firstId ? `/organizer/events/${firstId}` : undefined,
    },
    {
      id: 'registrations',
      label: 'Review registrations',
      done: regsDone,
      href: firstId ? `/organizer/events/${firstId}` : undefined,
    },
    {
      id: 'judges',
      label: 'Assign a judge',
      done: judgesDone,
      href: firstId ? `/organizer/events/${firstId}` : undefined,
    },
  ];
}

export function checklistProgress(steps: ChecklistStep[]): { done: number; total: number } {
  return { done: steps.filter((s) => s.done).length, total: steps.length };
}

/** Mature = vehicle present and most core checklist steps already done. */
export function isMatureBuilder(steps: ChecklistStep[]): boolean {
  const { done, total } = checklistProgress(steps);
  return total > 0 && done >= Math.max(4, total - 1);
}

export function shouldShowBuilderChecklist(
  state: OnboardingState,
  steps: ChecklistStep[]
): boolean {
  if (state.checklist_dismissed_at) return false;
  if (state.completed_at) return false;
  if (isMatureBuilder(steps) && checklistProgress(steps).done === checklistProgress(steps).total) {
    return false;
  }
  const { done, total } = checklistProgress(steps);
  return done < total;
}

export function shouldShowOnboardingResume(
  state: OnboardingState,
  steps: ChecklistStep[]
): boolean {
  if (state.completed_at) return false;
  if (!state.checklist_dismissed_at && !state.welcome_dismissed_at && !state.skipped_at) {
    return false;
  }
  const { done, total } = checklistProgress(steps);
  return total > 0 && done < total && !isMatureBuilder(steps);
}

export function shouldShowOrganizerChecklist(
  state: OnboardingState,
  steps: ChecklistStep[]
): boolean {
  if (state.organizer_checklist_dismissed_at) return false;
  const { done, total } = checklistProgress(steps);
  return done < total;
}

export function shouldShowJudgeGuide(state: OnboardingState): boolean {
  return !state.judge_guide_dismissed_at;
}

export function shouldShowClubGuide(state: OnboardingState): boolean {
  return !state.club_guide_dismissed_at;
}

export function shouldShowShopTip(state: OnboardingState): boolean {
  return !state.shop_tip_dismissed_at && !isTipDismissed(state, 'shops_directory');
}

export function resolveOnboardingRole(
  role?: string | null,
  caps?: { can_organize?: boolean; can_judge?: boolean } | null
): OnboardingRole {
  if (role === 'super_admin') return 'admin';
  if (caps?.can_organize || role === 'organizer') return 'organizer';
  if (caps?.can_judge || role === 'judge') return 'judge';
  return 'builder';
}
